// 裝置設定（spec 21）— capability-driven section registry（§2.2）。
// Mounted by DeviceDetailPage below the read-only info card.
//   - 只渲染 capability_effective 命中的 section；未知 cluster 整段隱藏並記
//     Settings.UnknownCluster（§2.4）；effective 為空（cold-start）由上層占位。
//   - Permission gate（§2.1）：device GET 無 shared permissions 明細，web v1 以
//     role 預判 — shared-user 一律視為無 control → 控制 section 整段隱藏
//     （非 disable）；權威永遠是 server fail-closed（ADR-009）。
//   - ADR-046：recording 三業務欄位 LS app 走 ws op:set（enumVal）；restful PUT 供 3rd-party。
//   - 即時讀寫走 Signaling room（useDeviceRoom 連線 + 重連自動 re-subscribe）。

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../../lib/api';
import type { DeviceSummary } from '../deviceModel';
import { accessibleChannels, canControlDevice, deviceName } from '../deviceModel';
import { AudioSection, DoorbellSection, FloodlightSection, SirenSection } from './AdvancedSections';
import { DetectionSection } from './DetectionSection';
import { ChannelSection } from './ChannelSection';
import { HealthSection } from './HealthSection';
import { WifiReconfigSection } from './WifiReconfigSection';
import { ImageSection, LightSection } from './ImageLightSection';
import { PtzSection } from './PtzSection';
import { RecordingSection } from './RecordingSection';
import { SdcardSection } from './SdcardSection';
import { PlugSection, SensorSection } from './SmartIotSections';
import { CL_AUDIO, CL_DOORBELL, CL_FLOODLIGHT, CL_HEALTH, CL_IMAGE, CL_LIGHT, CL_MOTION, CL_OBJECT, CL_PLUG, CL_PTZ, CL_RECORDING, CL_SDCARD, CL_SENSOR, CL_SIREN, CL_SOUND, CL_SOUNDRECOG, CL_TAMPER, CL_VIDEO_LOSS, CL_WIFI } from './ids';
import { unbindDevice, useDeviceRoom } from './ops';
import { ConfirmDialog, SectionCard } from './widgets';

// Clusters with a defined spec-21 section but not yet rendered in web
// (Streaming 0x80a0) → hidden silently; anything else unknown → hidden +
// Settings.UnknownCluster log. PTZ（0x8060, APP#11）、Doorbell/Floodlight/Audio/
// Siren（APP#40）、Plug（0x8001）/Sensor（0x8010）（APP#39, dictionary v1.6.0 補齊成員）
// now rendered → moved into RENDERED below.
const KNOWN_BUT_UNRENDERED = new Set(['0x80a0']);
const RENDERED = new Set([
  CL_MOTION,
  CL_OBJECT,
  CL_SOUND,
  CL_SOUNDRECOG,
  CL_TAMPER,
  CL_IMAGE,
  CL_LIGHT,
  CL_FLOODLIGHT,
  CL_AUDIO,
  CL_SIREN,
  CL_DOORBELL,
  CL_PTZ,
  CL_PLUG,
  CL_SENSOR,
  CL_RECORDING,
  CL_SDCARD,
  CL_HEALTH,
  CL_VIDEO_LOSS,
]);

export function DeviceSettings({
  device,
  onDeviceUpdate,
  onUnbound,
  onFirmwareUpdated,
}: {
  device: DeviceSummary;
  onDeviceUpdate: (d: DeviceSummary) => void;
  /** Called after a successful WSS unbind so the page can leave + drop the device. */
  onUnbound?: () => void;
  /** Called after OTA success (backend firmware_version already updated) so the parent
   *  can refetch the device — keeps the 裝置資訊 overview firmware/upgradable in sync. */
  onFirmwareUpdated?: () => void;
}) {
  const { t } = useTranslation();
  const wsState = useDeviceRoom(device.device_id);
  const wsOpen = wsState === 'open';
  const alive = useRef(true);

  // unbind（Scott：LS App 走 Signal；第三方才走 REST，owner-only）
  const [unbindOpen, setUnbindOpen] = useState(false);
  const [unbindBusy, setUnbindBusy] = useState(false);
  const [unbindError, setUnbindError] = useState<string | null>(null);

  // rename（spec 21 §4：PATCH /v3/devices/{id} display_name，owner-only，樂觀 + rollback）
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // per-user 別名（spec 21 §4：PATCH aliasname，任何 active 成員可設自己那列；空=清除）
  const [aliasing, setAliasing] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [aliasBusy, setAliasBusy] = useState(false);
  const [aliasError, setAliasError] = useState<string | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const clusters = useMemo(() => {
    const set = new Set<string>();
    for (const c of device.capability_effective ?? []) {
      if (c.cluster) set.add(c.cluster.toLowerCase());
    }
    return set;
  }, [device.capability_effective]);

  const subs = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of device.capability_effective ?? []) {
      if (c.cluster) map.set(c.cluster.toLowerCase(), c.sub ?? []);
    }
    return map;
  }, [device.capability_effective]);

  useEffect(() => {
    for (const c of clusters) {
      if (!RENDERED.has(c) && !KNOWN_BUT_UNRENDERED.has(c)) {
        console.info('Settings.UnknownCluster', { cluster: c, device_id: device.device_id });
      }
    }
  }, [clusters, device.device_id]);

  // APP#61：fail-closed（與 homeAwayApply.ts canControl 同 pattern）— 缺 role 不再視為 owner。
  // /v3/devices 每台恆帶 role（需有效 relation 才可見，00 §6.1 + ADR-009），此為防禦性強化、不影響既有裝置。
  // canControl（21 §2.1）：owner/admin 或 shared-user 具 `control` permission → §3/§5.1a 控制 widget。
  // isOwner：§4 display_name、§5.2 維運、§6 錄影設定、OTA、unbind 維持 owner-only（§9 彙整表）。
  const isOwner = device.role === 'owner' || device.role === 'admin';
  const canControl = canControlDevice(device);
  const name = deviceName(device);

  async function saveRename() {
    const next = nameDraft.trim();
    if (next.length < 1 || next.length > 32) {
      setRenameError(t('deviceSettings.nameLengthError'));
      return;
    }
    setRenameBusy(true);
    setRenameError(null);
    const prev = device.display_name;
    onDeviceUpdate({ ...device, display_name: next }); // optimistic
    try {
      await apiFetch(`/v3/devices/${encodeURIComponent(device.device_id)}`, { method: 'PATCH', body: { display_name: next } });
      if (!alive.current) return;
      setRenaming(false);
    } catch (err) {
      if (!alive.current) return;
      onDeviceUpdate({ ...device, display_name: prev }); // rollback
      setRenameError(err instanceof ApiError ? t('deviceSettings.renameFailedReason', { msg: err.message }) : t('deviceSettings.renameFailed'));
    } finally {
      if (alive.current) setRenameBusy(false);
    }
  }

  // 設定呼叫者自己的 per-user 別名（覆蓋 owner 預設；空字串=清除退回預設）。
  async function saveAlias() {
    const next = aliasDraft.trim();
    if (next.length > 32) {
      setAliasError(t('deviceSettings.aliasLengthError'));
      return;
    }
    setAliasBusy(true);
    setAliasError(null);
    const prev = device.aliasname;
    onDeviceUpdate({ ...device, aliasname: next }); // optimistic
    try {
      await apiFetch(`/v3/devices/${encodeURIComponent(device.device_id)}`, { method: 'PATCH', body: { aliasname: next } });
      if (!alive.current) return;
      setAliasing(false);
    } catch (err) {
      if (!alive.current) return;
      onDeviceUpdate({ ...device, aliasname: prev }); // rollback
      setAliasError(err instanceof ApiError ? t('deviceSettings.aliasFailedReason', { msg: err.message }) : t('deviceSettings.aliasFailed'));
    } finally {
      if (alive.current) setAliasBusy(false);
    }
  }

  async function runUnbind() {
    setUnbindBusy(true);
    setUnbindError(null);
    try {
      await unbindDevice(device.device_id);
      if (!alive.current) return;
      setUnbindOpen(false);
      onUnbound?.(); // 後端已清關係 → 離開本頁並從清單移除
    } catch (err) {
      if (!alive.current) return;
      const frame = (err as Error & { frame?: { code?: unknown } })?.frame;
      if (frame?.code === 'forbidden') setUnbindError(t('deviceSettings.unbindForbidden'));
      else if (frame?.code === 'device_not_found') {
        // 已不存在（他處已解綁）→ 等同成功，離開即可。
        setUnbindOpen(false);
        onUnbound?.();
        return;
      } else setUnbindError(err instanceof Error ? t('deviceSettings.unbindFailedReason', { msg: err.message }) : t('deviceSettings.unbindFailed'));
    } finally {
      if (alive.current) setUnbindBusy(false);
    }
  }

  const hasDetection = clusters.has(CL_MOTION) || clusters.has(CL_OBJECT) || clusters.has(CL_SOUND) || clusters.has(CL_SOUNDRECOG) || clusters.has(CL_TAMPER);

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-8)', maxWidth: 720, marginTop: 'var(--spacing-8)' }}>
      {!wsOpen && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {wsState === 'reconnecting' ? t('deviceSettings.channelReconnecting') : t('deviceSettings.channelConnecting')}
        </p>
      )}

      {/* ---- 一般（改名，§4；registry 順位 1） ---- */}
      {isOwner && (
        <SectionCard title={t('deviceSettings.general')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', flexWrap: 'wrap' }}>
            <span>{t('deviceSettings.deviceName')}</span>
            {renaming ? (
              <span style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={nameDraft} maxLength={32} onChange={(e) => setNameDraft(e.target.value)} aria-label={t('deviceSettings.deviceName')} style={{ width: 200 }} />
                <button className="btn-primary" disabled={renameBusy} onClick={() => void saveRename()}>
                  {renameBusy ? t('common.saving') : t('common.save')}
                </button>
                <button className="btn-ghost" disabled={renameBusy} onClick={() => setRenaming(false)}>
                  {t('common.cancel')}
                </button>
              </span>
            ) : (
              <span style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'center' }}>
                <span className="text-secondary">{name}</span>
                <button
                  className="btn-ghost"
                  style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
                  onClick={() => {
                    setNameDraft(name);
                    setRenaming(true);
                  }}
                >
                  {t('deviceSettings.rename')}
                </button>
              </span>
            )}
          </div>
          {renameError && (
            <p className="error-text" role="alert" style={{ margin: 0 }}>
              {renameError}
            </p>
          )}
        </SectionCard>
      )}

      {/* ---- 我的別名（per-user aliasname，§4；任何成員可設自己那列） ---- */}
      <SectionCard title={t('deviceSettings.myAlias')}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', flexWrap: 'wrap' }}>
          <span>
            {t('deviceSettings.myAlias')}
            <span className="text-secondary" style={{ display: 'block', fontSize: 'var(--font-size-sm)' }}>
              {device.display_name ? t('deviceSettings.aliasHintWithDefault', { name: device.display_name }) : t('deviceSettings.aliasHint')}
            </span>
          </span>
          {aliasing ? (
            <span style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={aliasDraft} maxLength={32} onChange={(e) => setAliasDraft(e.target.value)} aria-label={t('deviceSettings.myAlias')} placeholder={t('deviceSettings.aliasPlaceholder')} style={{ width: 200 }} />
              <button className="btn-primary" disabled={aliasBusy} onClick={() => void saveAlias()}>
                {aliasBusy ? t('common.saving') : t('common.save')}
              </button>
              <button className="btn-ghost" disabled={aliasBusy} onClick={() => setAliasing(false)}>
                {t('common.cancel')}
              </button>
            </span>
          ) : (
            <span style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'center' }}>
              <span className="text-secondary">{device.aliasname || t('deviceSettings.aliasNotSet')}</span>
              <button
                className="btn-ghost"
                style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
                onClick={() => {
                  setAliasDraft(device.aliasname ?? '');
                  setAliasing(true);
                }}
              >
                {device.aliasname ? t('common.edit') : t('common.set')}
              </button>
            </span>
          )}
        </div>
        {aliasError && (
          <p className="error-text" role="alert" style={{ margin: 0 }}>
            {aliasError}
          </p>
        )}
      </SectionCard>

      {/* ---- capability-driven sections（§2.2 registry 順位） ---- */}
      {clusters.has(CL_VIDEO_LOSS) && <ChannelSection device={device} wsOpen={wsOpen} />}
      {/* §3 控制類 section:21 §2.1 permission gate —— 無 `control` 一律**不渲染**（非 disable、
          非唯讀殘影);僅 §5.1 資訊、§6 現值、SD 卡狀態等唯讀面保留在下方。 */}
      {canControl && hasDetection && <DetectionSection deviceId={device.device_id} wsOpen={wsOpen} clusters={clusters} subs={subs} />}
      {canControl && clusters.has(CL_IMAGE) && <ImageSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_LIGHT) && <LightSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_FLOODLIGHT) && <FloodlightSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_AUDIO) && <AudioSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_SIREN) && <SirenSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_DOORBELL) && <DoorbellSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_PTZ) && <PtzSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {clusters.has(CL_RECORDING) && <RecordingSection deviceId={device.device_id} canEdit={isOwner} wsOpen={wsOpen} channels={accessibleChannels(device)} />}
      {clusters.has(CL_SDCARD) && (
        <SdcardSection
          deviceId={device.device_id}
          canControl={canControl}
          isOwner={isOwner}
          wsOpen={wsOpen}
          deviceName={name}
          recordLocationSdcard={device.recording?.location === 'sdcard'}
        />
      )}
      {/* 非相機裝置（APP#39）：Plug 插座 / Sensor 環境感測（同為 §3 控制類，同一 gate） */}
      {canControl && clusters.has(CL_PLUG) && <PlugSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {canControl && clusters.has(CL_SENSOR) && <SensorSection deviceId={device.device_id} wsOpen={wsOpen} />}
      {/* 裝置資訊/維運 + OTA：資訊與韌體區塊對所有 relation 可見（唯讀），指令 owner-only */}
      <HealthSection
        deviceId={device.device_id}
        isOwner={isOwner}
        wsOpen={wsOpen}
        model={device.model ?? ''}
        currentVersion={device.firmware_version ?? ''}
        deviceName={name}
        onFirmwareUpdated={onFirmwareUpdated}
      />

      {/* ---- 更換 Wi-Fi（重配，模式 A；device/09 §5.4，owner-only）---- */}
      {isOwner && clusters.has(CL_WIFI) && <WifiReconfigSection device={device} wsOpen={wsOpen} />}

      {/* ---- 解除綁定（00 §7.5，owner-only；LS App Signal）---- */}
      {isOwner && (
        <SectionCard title={t('deviceSettings.unbind')}>
          <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
            {t('deviceSettings.unbindDesc', { name })}
          </p>
          <div style={{ marginTop: 'var(--spacing-4)' }}>
            <button className="btn-danger" disabled={unbindBusy} onClick={() => setUnbindOpen(true)}>
              {t('deviceSettings.unbindThisDevice')}
            </button>
          </div>
          {unbindError && (
            <p className="error-text" role="alert" style={{ margin: 'var(--spacing-3) 0 0' }}>
              {unbindError}
            </p>
          )}
        </SectionCard>
      )}

      {unbindOpen && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.unbindConfirmTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('deviceSettings.unbindConfirmBody1', { name })}</p>
                <p style={{ marginBottom: 0 }}>{t('deviceSettings.unbindConfirmBody2')}</p>
              </>
            ),
            confirmLabel: t('deviceSettings.confirmUnbind'),
            danger: true,
            requireText: name,
            requireTextHint: t('deviceSettings.typeDeviceNameHint', { name }),
          }}
          busy={unbindBusy}
          onCancel={() => setUnbindOpen(false)}
          onConfirm={() => void runUnbind()}
        />
      )}
    </div>
  );
}
